'use client';

import { Children, CSSProperties, ReactElement, ReactNode, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import { useItemsPerView } from '../hooks/useItemsPerView';
import { IconButton } from '../IconButton/IconButton';

import styles from './CarouselV2.module.scss';

export interface CarouselV2Labels {
    /** Accessible name of the ‹ button, e.g. "Previous slide". */
    previous: string;
    /** Accessible name of the › button, e.g. "Next slide". */
    next: string;
}

export interface CarouselV2Props {
    children: ReactNode;
    className?: string;
    /** Move every so many ms; paused on hover. Off when not set. */
    autoplayInterval?: number;
    /** Slides visible at once from `md` up (one on phones). @default 1 */
    itemsPerView?: number;
    /** Translated accessible names of the arrows. */
    labels: CarouselV2Labels;
}

/** Rounds to two decimals for the progress bar width. */
function round(value: number): number {
    return Math.round(value * 100) / 100;
}

/**
 * Several slides per view (one on phones) with arrows and a progress bar, e.g. banners and recommendations.
 *
 * @example
 * <CarouselV2 itemsPerView={2} autoplayInterval={5000} labels={{ previous: t('…'), next: t('…') }}>
 *     {banners.map((banner) => <Banner key={banner.id} {...banner} />)}
 * </CarouselV2>
 */
export function CarouselV2({
    children,
    className,
    autoplayInterval,
    itemsPerView = 1,
    labels,
}: CarouselV2Props): ReactElement {
    const slides = Children.toArray(children);
    const perView = useItemsPerView(itemsPerView);
    const maxIndex = Math.max(0, slides.length - perView);
    const positions = maxIndex + 1;
    const [current, setCurrent] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const prev = (): void => setCurrent((i) => (i - 1 + positions) % positions);
    const next = (): void => setCurrent((i) => (i + 1) % positions);

    useEffect(() => {
        setCurrent((i) => Math.min(i, maxIndex));
    }, [maxIndex]);

    useEffect(() => {
        if (!autoplayInterval || isPaused || maxIndex === 0) {
            return;
        }

        const id = setTimeout(() => setCurrent((i) => (i + 1) % positions), autoplayInterval);

        return () => clearTimeout(id);
    }, [current, isPaused, maxIndex, positions, autoplayInterval]);

    const activeIndex = Math.min(current, maxIndex);
    const firstPct = round(Math.min(((activeIndex + perView) / slides.length) * 100, 100));

    return (
        <div
            className={classNames(styles.wrapper, className)}
            style={{ '--items-per-view': itemsPerView } as CSSProperties}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <div className={styles.viewport}>
                <div
                    className={styles.track}
                    style={{
                        transform: `translateX(calc(${-activeIndex} * (100% + var(--slide-gap, 0px)) / ${perView}))`,
                    }}
                >
                    {slides.map((slide, i) => (
                        <div key={i} className={styles.slide}>
                            {slide}
                        </div>
                    ))}
                </div>

                {maxIndex > 0 && (
                    <>
                        <IconButton
                            aria-label={labels.previous}
                            variant="outlined"
                            size="sm"
                            className={classNames(styles.btn, styles['btn-prev'])}
                            onClick={prev}
                        >
                            <ChevronLeft />
                        </IconButton>

                        <IconButton
                            aria-label={labels.next}
                            variant="outlined"
                            size="sm"
                            className={classNames(styles.btn, styles['btn-next'])}
                            onClick={next}
                        >
                            <ChevronRight />
                        </IconButton>
                    </>
                )}
            </div>

            {maxIndex > 0 && (
                <div className={classNames(styles.progress, 'mt-2')}>
                    <div className={styles['progress-first']} style={{ width: `${firstPct}%` }} />
                    <div className={styles['progress-second']} />
                </div>
            )}
        </div>
    );
}
